import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Loader2 } from 'lucide-react';

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { processGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      // Use window.location directly to ensure we capture the hash
      const hash = window.location.hash;
      const search = window.location.search;
      const pathname = window.location.pathname;
      const fullUrl = window.location.href;
      const fromMarketplace = sessionStorage.getItem('mkt_google') === '1' || pathname.startsWith('/marketplace');
      const loginPath = fromMarketplace ? '/marketplace/login' : '/login';
      
      console.log('AuthCallback: Full URL:', fullUrl);
      console.log('AuthCallback: Processing at pathname:', pathname);
      console.log('AuthCallback: hash:', hash, 'search:', search);
      
      // Check for errors in URL (Emergent Auth might return errors in hash or search)
      const hashParams = hash ? new URLSearchParams(hash.substring(1)) : new URLSearchParams();
      const searchParams = new URLSearchParams(search);
      
      const error = hashParams.get('error') || searchParams.get('error');
      const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');
      
      if (error) {
        console.error('AuthCallback: OAuth error received:', error, errorDescription);
        // Show error to user via toast or alert
        alert(`Authentication failed: ${errorDescription || error}`);
        navigate(loginPath, { replace: true });
        return;
      }
      
      let sessionId = null;
      
      // Try hash first (fragment) - this is where Emergent Auth puts it
      if (hash) {
        sessionId = hashParams.get('session_id');
        console.log('AuthCallback: Found session_id in hash:', sessionId ? 'yes' : 'no');
      }
      
      // Try search params if not found in hash
      if (!sessionId && search) {
        sessionId = searchParams.get('session_id');
        console.log('AuthCallback: Found session_id in search:', sessionId ? 'yes' : 'no');
      }
      
      if (sessionId) {
        try {
          console.log('AuthCallback: Processing Google callback with session_id...');
          const user = await processGoogleCallback(sessionId);
          console.log('AuthCallback: User authenticated successfully:', user?.email);
          
          // Clear the hash and search params from URL
          window.history.replaceState(null, '', pathname);
          
          // Small delay to ensure state is updated before navigation
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Navigate based on role
          if (fromMarketplace || ['marketplace_customer', 'marketplace_vendor'].includes(user?.role)) {
            navigate('/marketplace', { replace: true });
          } else if (user?.role === 'driver') {
            navigate('/driver', { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error('AuthCallback: Error processing callback:', error);
          console.error('AuthCallback: Error details:', error.response?.data || error.message);
          // Store error for display on login page
          const errorMsg = error.response?.data?.detail || error.message || 'Authentication failed';
          sessionStorage.setItem('authError', JSON.stringify(errorMsg));
          navigate(loginPath, { replace: true });
        }
      } else {
        console.log('AuthCallback: No session_id found in URL');
        console.log('AuthCallback: Full URL was:', fullUrl);
        // Check if there's an error but no session_id
        if (!error) {
          sessionStorage.setItem('authError', JSON.stringify('No session ID received from authentication service. Please try again.'));
        }
        navigate(loginPath, { replace: true });
      }
    };

    processCallback();
  }, [navigate, processGoogleCallback]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p className="text-slate-400">Completing sign in...</p>
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && isAuthenticated && allowedRoles && !allowedRoles.includes(user?.role)) {
      // Super admin can access everything (check both role and _actualRole for test mode transition)
      if (user?.role === 'super_admin' || user?._actualRole === 'super_admin') return;
      navigate('/unauthorized', { replace: true });
    }
  }, [loading, isAuthenticated, user, allowedRoles, navigate]);

  // Redirect pending admins to pending approval page (unless already there)
  useEffect(() => {
    if (!loading && isAuthenticated && user?.role === 'admin' && user?.approval_status === 'pending') {
      if (location.pathname !== '/pending-approval') {
        navigate('/pending-approval', { replace: true });
      }
    }
  }, [loading, isAuthenticated, user, navigate, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Iter 243 fix: session expired / unauthenticated user landed on a protected
  // route. Instead of rendering a blank screen (return null) and waiting on a
  // useEffect navigate to fire, we render <Navigate> immediately so the user
  // is sent to the marketplace landing (marketing-first), preserving the
  // intended destination so they can come back after login.
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/marketplace?session=expired"
        state={{ from: location }}
        replace
      />
    );
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Super admin can access everything (check both role and _actualRole for test mode transition)
    if (user?.role === 'super_admin' || user?._actualRole === 'super_admin') {
      return children;
    }
    return null;
  }

  return children;
};

export default ProtectedRoute;
